/**
 * PropertySlotLink — AD-036 (revision 2026-06-24) per-slot registry control.
 *
 * The owner's unified-Property-step ruling collapsed the old two boxes
 * (standalone "Registry property" + "Property details") into ONE box. This
 * component is the link-or-new control that lives at the TOP of EACH property
 * slot inside the Property-details card (PropertyCard), exactly per
 * `AVBP_UNIFIED_PROPERTY_STEP_MOCKUP_2026-06-24.html`.
 *
 * Each slot is in exactly one of three states (driven by `prop.propertyId` +
 * local `mode`):
 *
 *   1. LINKED  — `prop.propertyId` is set → a compact `LINKED · P##### · address
 *                · ACTIVE/ARCHIVED` row with Change/Unlink. The capture fields
 *                BELOW (rendered by PropertyCard) write through to this doc.
 *   2. CHOOSE  — empty slot → a single search input ("Link an existing property —
 *                type P-number or address…") that does a GLOBAL search (this
 *                client's surfaced first) + a dropdown of matches; "— or —
 *                ＋ Enter a new property" reveals the capture fields below.
 *   3. NEW     — staff chose "enter a new property" → the capture fields below
 *                are active; on save we run the 25 m proximity check (offer
 *                "Use this" for a nearby match) → staff-confirm → createProperty()
 *                mints the next P-number, registers, and links.
 *
 * Heavy capture UI (Maps link / coords / north / floors / media) stays in
 * PropertyCard; this is the registry orchestration only. Pure logic lives in
 * propertyProximity.ts + propertyPicker.ts (unit-tested).
 */
import { useMemo, useState } from 'react';
import { Btn3, Tag } from './ui';
import type { IntakeScope } from '../lib/intakeScope';
import type { PropertyInput } from '../lib/intake';
import { savePropertyLink, propertyInputToSeed } from '../lib/intake';
import { upsertPropertyFromIntake, linkPropertyToClient } from '../lib/propertyStore';
import { linkPropertyAsCandidate } from '../lib/linkPropertyAsCandidate';
import {
  propertyCityLabel,
  type LiveProperty,
  type PropertySeed,
} from '../lib/propertyModel';
import {
  nearbyProperties,
  DEFAULT_PROXIMITY_RADIUS_M,
  type LatLng,
  type NearbyMatch,
} from '../lib/propertyProximity';
import {
  orderForPicker,
  filterProperties,
  type PropertyListItem,
} from '../lib/propertyPicker';

export interface PropertySlotLinkProps {
  /** The slot's property input (carries `propertyId` when already linked). */
  prop: PropertyInput;
  /** All registry properties (one shared listener lives in the parent). */
  allProps: LiveProperty[];
  scope: IntakeScope;
  tier: string;
  actorUid: string;
  /** Whether this slot is the PRIMARY (slot 0) — only the primary mirrors the
   * link into `answers.propertyId` for report-source resolution. */
  isPrimary: boolean;
  /** Patch THIS slot's PropertyInput (e.g. stamp / clear `propertyId`). */
  onPatchSlot: (patch: Partial<PropertyInput>) => void;
  /** Toast sink. */
  onMsg: (m: string, ok?: boolean) => void;
  /** Proximity radius override (settings-adjustable). Defaults to 25 m. */
  radiusM?: number;
  /**
   * Controlled "staff is entering a brand-new property" state. Lifted to the
   * parent (L1APropertyDetails) so it can also gate the capture fields below:
   * the capture body is only shown when the slot is LINKED or entering-new.
   */
  enteringNew: boolean;
  onEnteringNewChange: (v: boolean) => void;
}

/** Pull a {lat,lng} from a slot's location, or null. */
function slotCoord(prop: PropertyInput): LatLng | null {
  const seed = propertyInputToSeed(prop);
  const sl = seed.siteLocation;
  if (sl && typeof sl.lat === 'number' && typeof sl.lng === 'number') {
    return { lat: sl.lat, lng: sl.lng };
  }
  return null;
}

export default function PropertySlotLink({
  prop,
  allProps,
  scope,
  tier,
  actorUid,
  isPrimary,
  onPatchSlot,
  onMsg,
  radiusM = DEFAULT_PROXIMITY_RADIUS_M,
  enteringNew,
  onEnteringNewChange,
}: PropertySlotLinkProps) {
  const clientId = scope.clientId;
  const linkedId = prop.propertyId?.trim() || '';

  // CHOOSE-state local UI: search text. "Entering new" is controlled by the
  // parent (it also gates the capture body), so use the prop, not local state.
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const setEnteringNew = onEnteringNewChange;

  const linkedProp = useMemo(
    () => allProps.find((p) => p.propertyId === linkedId) ?? null,
    [allProps, linkedId],
  );

  // Search list — this client's own first, then global; filtered by the query.
  const listItems = useMemo<PropertyListItem[]>(() => {
    const items = allProps.map((p) => ({
      propertyId: p.propertyId,
      label: p.address || p.propertyId,
      city: propertyCityLabel(p),
      ownedByClient: (p.clientRefs ?? []).some((r) => r.clientId === clientId),
      archived: p.archived === true,
    }));
    return orderForPicker(filterProperties(items, search));
  }, [allProps, clientId, search]);

  // Proximity matches for the slot's current coordinates (NEW state only).
  const coord = slotCoord(prop);
  const matches = useMemo<NearbyMatch[]>(() => {
    if (!coord || linkedId) return [];
    return nearbyProperties(coord, allProps, radiusM, linkedId || undefined);
  }, [coord, allProps, radiusM, linkedId]);

  async function doLink(propertyId: string) {
    setBusy(true);
    try {
      await linkPropertyToClient(propertyId, clientId);
      // Gap #2 — ALSO write project.propertyRefs[] (+ engagementRefs) so a slot-
      // linked property shows in the Linked-Properties panel. ONE unified path:
      // ensureCandidate:false because THIS rendered slot IS the candidate (stamped
      // via onPatchSlot below) — appending would duplicate it. Project-scoped only,
      // and NON-FATAL: a project-ref write must never block the slot stamp below.
      if (scope.projectId) {
        await linkPropertyAsCandidate(
          scope.projectId,
          clientId,
          propertyId,
          isPrimary ? 'primary' : 'candidate',
          tier,
          { ensureCandidate: false },
        ).catch(() => { /* non-fatal — slot stamp + client link still apply */ });
      }
      // The PRIMARY slot also mirrors into answers.propertyId so the report
      // engine's resolvePropertySource() keeps reading the entity path.
      if (isPrimary) await savePropertyLink(scope, tier, propertyId, null, actorUid);
      onPatchSlot({ propertyId });
      setEnteringNew(false);
      setSearch('');
      onMsg(`Linked ${propertyId}.`, true);
    } catch {
      onMsg('Could not link the property — check your connection.');
    } finally {
      setBusy(false);
    }
  }

  async function doCreate() {
    setBusy(true);
    try {
      const seed = propertyInputToSeed(prop) as PropertySeed;
      const { propertyId, created } = await upsertPropertyFromIntake(
        seed,
        { clientId, projectId: scope.projectId, role: isPrimary ? 'primary' : 'candidate' },
        actorUid,
      );
      // Gap #2 — ALSO write project.propertyRefs[] so a newly-created slot property
      // shows in the Linked-Properties panel. ensureCandidate:false (the slot IS
      // the candidate). engagementRefs already written by upsertPropertyFromIntake;
      // linkPropertyToProject is idempotent so it won't duplicate that side.
      // NON-FATAL — must never block the slot stamp below.
      if (scope.projectId) {
        await linkPropertyAsCandidate(
          scope.projectId,
          clientId,
          propertyId,
          isPrimary ? 'primary' : 'candidate',
          tier,
          { ensureCandidate: false },
        ).catch(() => { /* non-fatal — created property + slot stamp still apply */ });
      }
      if (isPrimary) await savePropertyLink(scope, tier, propertyId, null, actorUid);
      onPatchSlot({ propertyId });
      setEnteringNew(false);
      onMsg(created ? `Created & linked ${propertyId}.` : `Linked ${propertyId}.`, true);
    } catch {
      onMsg('Could not create the property — check your connection.');
    } finally {
      setBusy(false);
    }
  }

  async function doUnlink() {
    onPatchSlot({ propertyId: undefined });
    setEnteringNew(false);
    if (isPrimary) {
      await savePropertyLink(scope, tier, '', null, actorUid).catch(() => {});
    }
    onMsg('Unlinked. Search to link another, or enter a new property.');
  }

  // AD-036.3 — WRITE-THROUGH for a LINKED slot. The capture fields below a linked
  // slot (address / site-location / north / area) edit the local form state only;
  // this pushes those identity edits into the registry doc so it stays current.
  // Reuses the SAME `upsertPropertyFromIntake` write-through branch (passing the
  // linked id) as the create path — no parallel write. `updatePropertyIdentity`
  // patches ONLY identity fields (address/addressComps/siteLocation/projectType/
  // northRotation); floors / plans / media on the property doc are never touched
  // (the intake seed carries no such keys).
  async function doSaveToRegistry() {
    if (!linkedId) return;
    setBusy(true);
    try {
      const seed = propertyInputToSeed(prop) as PropertySeed;
      await upsertPropertyFromIntake(
        seed,
        { clientId, projectId: scope.projectId, role: isPrimary ? 'primary' : 'candidate' },
        actorUid,
        linkedId,
      );
      onMsg(`Saved to ${linkedId} — registry updated.`, true);
    } catch {
      onMsg('Could not save to the registry — check your connection.');
    } finally {
      setBusy(false);
    }
  }

  // ── LINKED ────────────────────────────────────────────────────────────────
  if (linkedId) {
    const label = linkedProp?.address || linkedId;
    const archived = linkedProp?.archived === true;
    return (
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            background: 'var(--warn-wash, #fbf3e1)',
            border: '1px solid var(--warn, #eccf93)',
            borderRadius: 'var(--r-sm)',
            padding: '9px 11px',
          }}
        >
          <Tag kind="val">LINKED</Tag>
          <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{linkedId}</span>
          <span style={{ fontSize: 12.5, flex: 1, minWidth: 120 }}>{label}</span>
          <Tag kind={archived ? 'warn' : 'val'}>{archived ? 'ARCHIVED' : 'ACTIVE'}</Tag>
          <Btn3 intent="ink" size="sm" onClick={doSaveToRegistry} disabled={busy}>
            {busy ? 'Saving…' : 'Save to registry'}
          </Btn3>
          <Btn3 intent="ghost" size="sm" onClick={doUnlink} disabled={busy}>
            Change / Unlink
          </Btn3>
        </div>
        <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '6px 2px 0' }}>
          Edit the details below, then <b>Save to registry</b> to write the address / location /
          north / area through to <b>{linkedId}</b>. Drawings, floors &amp; media stay on the
          intake — only the property identity is updated.
        </p>
      </div>
    );
  }

  // ── NEW (capture fields below are active; proximity-guarded create) ─────────
  if (enteringNew) {
    return (
      <div style={{ marginBottom: 12 }}>
        {matches.length > 0 && (
          <div
            style={{
              border: '1px solid var(--warn, #eccf93)',
              background: 'var(--warn-wash, #fdf6e8)',
              borderRadius: 'var(--r-sm)',
              padding: '10px 12px',
              marginBottom: 10,
            }}
          >
            <p style={{ margin: '0 0 6px', fontSize: 12.5, fontWeight: 700, color: 'var(--warn-deep, #9a6a12)' }}>
              ⚠ We already have {matches.length} propert{matches.length === 1 ? 'y' : 'ies'} within {radiusM} m of this location:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {matches.map((m) => (
                <div key={m.property.propertyId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                    {m.property.propertyId}
                  </span>
                  <span style={{ fontSize: 12.5, flex: 1 }}>
                    {m.property.address || '(no address)'} · {m.distanceM} m away
                  </span>
                  <Tag kind={m.archived ? 'warn' : 'val'}>{m.archived ? 'ARCHIVED' : 'ACTIVE'}</Tag>
                  <Btn3 intent="ink" size="sm" onClick={() => doLink(m.property.propertyId)} disabled={busy}>
                    Use this
                  </Btn3>
                </div>
              ))}
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '7px 0 0' }}>
              Not the same place? Continue below to create a new property.
            </p>
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn3 intent="grn" size="sm" onClick={doCreate} disabled={busy || !coord}>
            {busy ? 'Creating…' : 'Create & link as new property'}
          </Btn3>
          <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
            {coord
              ? 'Staff confirms — mints the next P-number & registers it.'
              : 'Enter the Maps link / coordinates below first.'}
          </span>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setEnteringNew(false)}
            disabled={busy}
            style={{ marginLeft: 'auto' }}
          >
            Cancel
          </Btn3>
        </div>
      </div>
    );
  }

  // ── CHOOSE (search to link, or "enter a new property") ──────────────────────
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Link an existing property — type P-number or address…"
          autoComplete="off"
          aria-label="Link an existing property"
          style={{
            width: '100%',
            padding: '10px 12px',
            fontSize: 13,
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
          }}
        />
        {search.trim() && (
          <div
            style={{
              border: '1px solid var(--rule)',
              borderTop: 'none',
              borderRadius: '0 0 var(--r-sm) var(--r-sm)',
              background: 'var(--surface, #fff)',
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {listItems.length > 0 ? (
              listItems.slice(0, 30).map((it) => (
                <button
                  key={it.propertyId}
                  type="button"
                  onClick={() => doLink(it.propertyId)}
                  disabled={busy}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    textAlign: 'left',
                    padding: '9px 12px',
                    border: 'none',
                    borderTop: '1px solid var(--rule)',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--accent)' }}>
                    {it.propertyId}
                  </span>
                  <span style={{ fontSize: 13, flex: 1 }}>
                    {it.label}
                    {it.city ? <span style={{ color: 'var(--ink-mute)' }}> · {it.city}</span> : null}
                  </span>
                  {it.ownedByClient && <Tag kind="val">This client</Tag>}
                  <Tag kind={it.archived ? 'warn' : 'val'}>{it.archived ? 'ARCHIVED' : 'ACTIVE'}</Tag>
                </button>
              ))
            ) : (
              <div style={{ padding: '9px 12px', fontSize: 12, color: 'var(--ink-mute)' }}>
                No saved property matches “{search.trim()}”. Use “Enter a new property”.
              </div>
            )}
          </div>
        )}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: 'var(--ink-mute)',
          fontSize: 12,
          margin: '11px 0',
        }}
      >
        <span style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
        or
        <span style={{ flex: 1, height: 1, background: 'var(--rule)' }} />
      </div>

      <Btn3 intent="ghost" size="sm" onClick={() => setEnteringNew(true)} disabled={busy}>
        ＋ Enter a new property
      </Btn3>
    </div>
  );
}
